import type { UppyFile } from '@uppy/core'
import { CmFieldResponseObject } from '../../../interfaces'
import * as Yup from 'yup'
import { CmFieldTypeEnum, PostTransformationTypes } from '../../../constants'
import moment from 'moment'
import _, { isNull, isUndefined } from 'lodash'
import { AutoTaggingObj, ExtensionData, Extensions, RemoveBgProps } from '../../../interfaces/SettingsResponse'

interface FieldRenderOptions {
  // copied from uppy d.ts file
  value: string
  fieldCSSClasses: { text: string }
  required: boolean
  form: string
  onChange(newVal: string): void
}

type PreactRender = (
  // copied from uppy d.ts file
  node: any,
  params: Record<string, unknown> | null,
  ...children: any[]
) => any

interface AllUppyFiles {
  [key: string]: UppyFile
}

interface FileWithRelativePath extends File {
  webkitRelativePath: string
}

const metaFields = [
  {
    id: 'name',
    name: 'File name',
    placeholder: 'Enter the file name',
  },
  {
    id: 'useUniqueFileName',
    name: 'Avoid overwriting existing files by changing file name',
    render({ value, onChange }: FieldRenderOptions, h: PreactRender) {
      return h('input', {
        type: 'checkbox',
        onChange: (ev: React.ChangeEvent<HTMLInputElement>) =>
          void onChange(ev.target.checked ? 'true' : 'false'),
        checked: value === 'true',
        style: {
          verticalAlign: 'middle',
          width: '13px',
        },
      }) as HTMLInputElement
    },
  },
  {
    id: 'isPrivateFile',
    name: 'Private File',
    render(
      { value, onChange }: FieldRenderOptions,
      h: PreactRender,
    ): HTMLInputElement {
      return h('input', {
        type: 'checkbox',
        onChange: (ev: React.ChangeEvent<HTMLInputElement>) =>
          void onChange(ev.target.checked ? 'true' : 'false'),
        checked: value === 'true',
        style: {
          verticalAlign: 'middle',
          width: '13px',
        },
      }) as HTMLInputElement
    },
  },
  {
    id: 'isPublished',
    name: 'Publish File',
    render(
      { value, onChange }: FieldRenderOptions,
      h: PreactRender,
    ): HTMLInputElement {
      return h('input', {
        type: 'checkbox',
        onChange: (ev: React.ChangeEvent<HTMLInputElement>) =>
          void onChange(ev.target.checked ? 'true' : 'false'),
        checked: value === 'true',
        style: {
          verticalAlign: 'middle',
          width: '13px',
        },
      }) as HTMLInputElement
    },
  },
  {
    id: 'tags',
    name: 'Tags',
    placeholder: 'Comma seperated tags e.g. t-shirt,summer',
  },
  {
    id: 'customCoordinates',
    name: 'Custom Focus Area',
    placeholder: 'Comma seperated values in format x,y,width,height',
  },
  {
    id: 'folder',
    name: 'Folder Path',
    render({ value }: FieldRenderOptions, h: PreactRender): HTMLInputElement {
      return h('input', {
        type: 'text',
        class: 'uppy-u-reset uppy-c-textInput uppy-Dashboard-FileCard-input',
        disabled: true,
        value,
        'data-uppy-super-focusable': true,
        placeholder: 'Folder to upload file into',
        style: {
          backgroundColor: '#f3f4f5',
        },
      }) as HTMLInputElement
    },
  },
]

interface allowedStaticFilesInterface {
  image: string[]
  video: string[]
  audio: string[]
  raw: string[]
}

const allowedStaticFiles: allowedStaticFilesInterface = {
  image: [
    'image/webp',
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/gif',
    'image/svg+xml',
    'image/avif',
    'image/heif',
    'image/heic',
  ],
  video: [
    'video/mp4',
    'video/x-m4v',
    'video/webm',
    'video/quicktime',
    'application/x-shockwave-flash',
    'video/MP2T',
    'application/x-mpegURL',
    'application/x-mpegurl',
  ],
  audio: ['audio/mpeg', 'audio/x-m4a', 'audio/wav'],
  raw: [
    'application/octet-stream',
    'application/pdf',
    'application/x-javascript',
    'application/javascript',
    'text/javascript',
    'text/js',
    'text/css',
    'text/plain',
    'font/woff2',
    'font/woff',
    'application/font-woff',
    'font/ttf',
    'font/otf',
    '.woff2',
    '.woff',
    '.ttf',
    '.otf',
    '.eot',
    'application/vnd.ms-fontobject',
    'application/json',
    'application/xml',
    'text/xml',
    'image/vnd.adobe.photoshop',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/postscript',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/rtf',
    'text/rtf',
    'application/x-httpd-php',
    'application/rss+xml',
    'text/html',
    'image/bmp',
    'image/tiff',
    'model/gltf-binary',
    'image/vnd.dxf',
    'application/eps',
    'model/obj',
    'model/stl',
    'image/x-3ds',
    'image/x-adobe-dng',
    'image/vnd.djvu',
    'model/gltf+json',
    'application/x-indesign',
    'image/x-sony-arw',
    'text/csv',
    'text/yaml',
    'application/x-yaml',
    'text/x-python-script',
    'text/x-python',
    'application/x-python-code',
    'text/php',
  ],
}

const fileConstraints = {
  maxSize: {
    image: 25 * 1024 * 1024,
    video: 25 * 1024 * 1024,
    audio: 25 * 1024 * 1024,
    raw: 25 * 1024 * 1024,
  },
  allowedFiles: allowedStaticFiles,
  maxNumberOfFiles: 5000,
  timeout: 120000,
}

// const userPermissions = window.user.permissions.split(",") || [];

const hasPermission = (
  permissionName: string,
  allPermissions: string,
): boolean => {
  const userPermissions = allPermissions.split(',') ?? []
  return userPermissions.includes(permissionName)
}

function ifBothFilesAreInSameDirectory(
  currentFile: UppyFile,
  fileToCompare: UppyFile,
): boolean {
  let isSameDirectory = false

  let currentFileRelativePathToCompare = currentFile.meta.relativePath

  if (!currentFileRelativePathToCompare) {
    currentFileRelativePathToCompare = `/${currentFile.name}`
  }

  if (
    currentFileRelativePathToCompare &&
    fileToCompare.meta.relativePath &&
    currentFileRelativePathToCompare === fileToCompare.meta.relativePath
  ) {
    isSameDirectory = true
  }

  return isSameDirectory
}

function checkIfFileNamePresent(
  currentFile: UppyFile,
  files: AllUppyFiles,
): boolean {
  const filesArr = Object.values(files)

  let fileNameAlreadyPresent = false

  for (const fileItem of filesArr) {
    if (
      fileItem.name === currentFile.name &&
      ifBothFilesAreInSameDirectory(currentFile, fileItem)
    ) {
      fileNameAlreadyPresent = true
      break
    }
  }
  return fileNameAlreadyPresent
}

function getModifiedName(currentName: string): string {
  const nameArr = currentName.split('.')
  let nameWithoutExt = nameArr[0]

  let noToAdd = 1

  if (nameWithoutExt.endsWith(')')) {
    nameWithoutExt = nameWithoutExt.slice(0, -1)
    const matches = /\d+$/u.exec(nameWithoutExt)
    if (matches) {
      const numInEnd = matches[0]
      const integerInEnd = parseInt(numInEnd, 10)

      const lettersToRemove = numInEnd.length
      nameWithoutExt = nameWithoutExt.slice(0, -lettersToRemove)
      if (nameWithoutExt.endsWith('(')) {
        nameWithoutExt = nameWithoutExt.slice(0, -1)
        // valid acc to our req
        noToAdd = integerInEnd + 1
      }
    }
  }

  const newName = `${nameWithoutExt}(${noToAdd}).${nameArr[1]}`
  return newName
}

function addIncrementInFileNameIfRequired(
  currentFile: UppyFile,
  files: AllUppyFiles,
): string {
  const currentName = currentFile.name

  if (checkIfFileNamePresent(currentFile, files)) {
    const newName = getModifiedName(currentName)
    const modifiedFile = {
      ...currentFile,
      name: newName,
      meta: {
        ...currentFile.meta,
        name: newName,
      },
    }
    return addIncrementInFileNameIfRequired(modifiedFile, files)
  }
  return currentName
}

function getFolderPathForFile(
  folderToUpload: string,
  fileWebkitRelativePath: string | undefined,
): string {
  let directoryStructure: string

  if (fileWebkitRelativePath) {
    directoryStructure = fileWebkitRelativePath
    const directoryStructureArr = directoryStructure.split('/')
    directoryStructureArr.pop()
    directoryStructure = directoryStructureArr.join('/')

    if (directoryStructure.startsWith('/')) {
      directoryStructure = directoryStructure.substring(1)
    }
    if (directoryStructure.endsWith('/')) {
      directoryStructure = directoryStructure.substring(
        0,
        directoryStructure.length - 1,
      )
    }

    if (!directoryStructure) {
      return folderToUpload
    }

    if (folderToUpload !== '/') {
      folderToUpload = `${folderToUpload}/`
    }

    return `${folderToUpload}${directoryStructure}`
  }

  return folderToUpload
}

// folder upload functions
// let FileSystemDirectoryEntry = window.FileSystemDirectoryEntry || window.DirectoryEntry;
// let FileSystemEntry = window.FileSystemEntry || window.Entry;

// Wrap readEntries in a promise to make working with readEntries easier
async function readEntriesPromise(
  directoryReader: DirectoryReader,
): Promise<FileSystemEntry[] | [] | undefined | null> {
  try {
    return await new Promise<FileSystemEntry[] | []>((resolve, reject) => {
      directoryReader.readEntries(resolve, reject)
    })
  } catch (e) {
    if (e instanceof Error) {
      // console.log(e)
    }
  }
  return null
}

// Get all the entries (files or sub-directories) in a directory by calling readEntries until it returns empty array
async function readAllDirectoryEntries(
  directoryReader: DirectoryReader,
): Promise<FileSystemEntry[]> {
  const entries: FileSystemEntry[] = []
  let readEntries = await readEntriesPromise(directoryReader)
  while (readEntries && readEntries.length > 0) {
    entries.push(...readEntries)
    readEntries = await readEntriesPromise(directoryReader)
  }
  return entries
}

// Drop handler function to get all files
async function getAllFileEntries(
  dataTransferItemList: DataTransferItemList,
): Promise<FileSystemEntry[]> {
  const fileEntries: FileSystemEntry[] = []
  // Use BFS to traverse entire directory/file structure
  const queue: FileSystemEntry[] = []
  // Unfortunately dataTransferItemList is not iterable i.e. no forEach
  for (let index = 0; index < dataTransferItemList.length; index++) {
    const entryItem = dataTransferItemList[index].webkitGetAsEntry()
    if (entryItem) {
      queue.push(entryItem)
    }
  }
  while (queue.length > 0) {
    const entry: FileSystemEntry | undefined = queue.shift()

    if (entry?.isFile) {
      fileEntries.push(entry as FileEntry)
    } else if (entry?.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader()
      queue.push(...(await readAllDirectoryEntries(reader)))
    }
  }
  return fileEntries
}

async function getFileFromFileEntry(
  fileEntry,
): Promise<FileWithRelativePath | undefined> {
  try {
    return await new Promise((resolve, reject) =>
      fileEntry.file(resolve, reject),
    )
  } catch (err) {
    // console.log(err)
  }
}

function getFileVersionFolderPath(filePath): string {
  let directory = filePath.split('/')

  directory.pop()
  directory = directory.join('/')

  if (!directory.startsWith('/') || directory.trim() === '') {
    return `/${directory.trim()}`
  }
  return directory
}

function getCmFieldValidatorMap(cmFields: CmFieldResponseObject[]) {
  const cmFieldValidatorMap = new Map<string, Yup.AnySchema>(
    cmFields.map(cmField => {
      let validator: Yup.AnySchema
      switch (cmField.schema.type) {
        case CmFieldTypeEnum.Boolean:
          validator = Yup.boolean()
          break
        case CmFieldTypeEnum.Number:
          validator = Yup.number()
            .typeError('Must be a number')
            .min(
              typeof cmField.schema.minValue === 'number' ? (parseFloat(`${cmField.schema.minValue}`)) : -Infinity,
              `Minimum ${cmField.schema.minValue}`,
            )
            .max(
              typeof cmField.schema.maxValue === 'number' ? (parseFloat(`${cmField.schema.maxValue}`)) : Infinity,
              `Maximum ${cmField.schema.maxValue}`,
            )
          break
        case CmFieldTypeEnum.Text:
        case CmFieldTypeEnum.Textarea:
          validator = Yup.string()
            .min(
              cmField.schema.minLength ?? 0,
              `Minimum ${cmField.schema.minLength} characters required`,
            )
            .max(
              cmField.schema.maxLength ?? 1024,
              `Maximum ${cmField.schema.maxLength} characters required`,
            )
          break
        case CmFieldTypeEnum.Date:
          validator = Yup.date()
          validator = _.isString(cmField.schema.minValue)
            ? (validator as Yup.DateSchema).min(
                new Date(cmField.schema.minValue),
                `Date should be on or after ${moment(
                  new Date(cmField.schema.minValue),
                ).format('Do MMM YY')}`,
              )
            : validator
          validator = _.isString(cmField.schema.maxValue)
            ? (validator as Yup.DateSchema).max(
                new Date(cmField.schema.maxValue),
                `Date should be on or before ${moment(
                  new Date(cmField.schema.maxValue),
                ).format('Do MMM YY')}`,
              )
            : validator
          break
        case CmFieldTypeEnum.SingleSelect:
          validator = Yup.mixed().test("is-valid-option", "Must be one of the valid options", (value) => {
            // Allow empty/undefined values if field is not required
            if (!cmField.schema.isValueRequired && (value === undefined || value === null || value === '')) {
              return true
            }
            return new Set(cmField.schema.selectOptions).has(value)
          })
          break
        case CmFieldTypeEnum.MultiSelect:
          validator = Yup.array(
            Yup.mixed().test("is-valid-option", "Must be one of the valid options", (value) => {
              // Allow empty/undefined values if field is not required
              if (!cmField.schema.isValueRequired && (value === undefined || value === null || value === '')) {
                return true
              }
              return new Set(cmField.schema.selectOptions).has(value)
            })
          )
          break
        default:
          throw Error('Should not happen')
      }

      return [cmField.name, validator]
    }),
  )

  return cmFieldValidatorMap
}

const checkCfRequired = customCoordinates => {
  if (customCoordinates) {
    // check all are undefined or null
    let countNullField = 0
    let countValidField = 0
    Object.keys(customCoordinates)?.forEach(key => {
      if (!customCoordinates[key]) countNullField = countNullField + 1
      else countValidField = countValidField + 1
    })
    if (!(countNullField === 4 || countValidField === 4)) return true
    return false
  }
}

export const validatePostTransformation = postTransformation => {
  let error
  if (postTransformation) {
    if (!postTransformation.type) {
      error = `Type is required.`
    } else if (
      postTransformation.type === PostTransformationTypes.ABS &&
      !postTransformation.protocol
    ) {
      error = 'Protocol is required for ABS type transformation.'
    } else if (
      (postTransformation.type === PostTransformationTypes.Transformation ||
        postTransformation.type === PostTransformationTypes.ABS) &&
      !postTransformation.value
    ) {
      error = `Value is required.`
    }
  }
  return error
}

const validateCmField = async (
  formValues,
  cmFields,
  cmFieldValidatorMap,
  isUpload,
) => {
  
  const fieldEntries = Object.entries(formValues)
    .filter(
      ([fieldName]) => cmFieldValidatorMap.has(fieldName), // if value belongs to deleted metadata
    )
    .map(([fieldName, value]) => {
      if (isNull(value)) {
        return [fieldName, undefined]
      }
      return [fieldName, value]
    }) as Array<[string, unknown]>

  const fieldErrors = {}

  // Check all mandatory fields are added
  cmFields
    .filter(field => field.schema.isValueRequired)
    .forEach(field => {
      if (
        isUndefined(formValues[field.name]) ||
        isNull(formValues[field.name])
      ) {
        fieldErrors[field.name] = 'Mandatory field'
      }
    })

    fieldEntries
    .filter(([field, value]) => typeof value === "string")
    .forEach(([field, value]) => {
      if (
        (value as string).trim()===""
      ) {
        fieldErrors[field] = 'Must be non empty string with at least one non-space character.'
      }
    })
 
  // Validate each field & populate fieldErrors
  await Promise.allSettled(
    fieldEntries.map(([fieldName, value]) =>
      cmFieldValidatorMap
        .get(fieldName)
        ?.validate(value)
        .then(validValue => {
          return [fieldName, validValue] as [string, unknown]
        })
        .catch(error => {
          fieldErrors[fieldName] = error.message
        }),
    ),
  )

  if (formValues && formValues?.customCoordinatesOb) {
    // check all are undefined or null
    let countNullField = 0
    let countValidField = 0
    Object.keys(formValues?.customCoordinatesOb)?.forEach(key => {
      if (!formValues?.customCoordinatesOb[key])
        countNullField = countNullField + 1
      else countValidField = countValidField + 1
    })
    if (!(countNullField === 4 || countValidField === 4)) {
      const customCoordinatesOb = {}
      Object.keys(formValues?.customCoordinatesOb)?.forEach(key => {
        if (!formValues?.customCoordinatesOb[key])
          customCoordinatesOb[key] = 'Mandatory field'
      })
      fieldErrors['customCoordinatesOb'] = customCoordinatesOb
    }
  }
  if (formValues && formValues?.postTransformation?.length) {
    for (let data of formValues.postTransformation) {
      if (validatePostTransformation(data)) {
        fieldErrors['postTransformation'] = validatePostTransformation(data)
        break
      }
    }
  }

  return isUpload
    ? fieldErrors && Object.keys(fieldErrors)?.length
      ? { upload: fieldErrors }
      : {}
    : fieldErrors
}

const formatTags = (fileObject, file) => {
  return {
    ...fileObject,
    tags: file.tags.split(',').map(data => ({ label: data, value: data })),
  }
}

const formatCustomMetadata = (fileObject, file, cmName, originalName) => {
  return (fileObject = {
    ...fileObject,
    [cmName]: JSON.parse(file?.customMetadata)[originalName],
  })
}

const validateCustomMetadata = (file, cmName) => {
  return (
    file?.customMetadata &&
    typeof file.customMetadata === 'string' &&
    Object.keys(JSON.parse(file.customMetadata)).length &&
    !(
      isUndefined(JSON.parse(file?.customMetadata)[cmName]) ||
      isNull(JSON.parse(file?.customMetadata)[cmName])
    )
  )
}

const validateCommonSettingCustomCoordinates = customCoordinates => {
  return (
    customCoordinates &&
    !(
      isUndefined(
        customCoordinates['x-coordinate'],
      ) ||
      isNull(customCoordinates['x-coordinate'])
    ) &&
    !(
      isUndefined(
        customCoordinates['y-coordinate'],
      ) ||
      isNull(customCoordinates['y-coordinate'])
    ) &&
    !(
      isUndefined(customCoordinates?.height) ||
      isNull(customCoordinates?.height)
    ) &&
    !(
      isUndefined(customCoordinates?.width) ||
      isNull(customCoordinates?.width)
    ) &&
    (
      customCoordinates?.['x-coordinate'] ||
      customCoordinates?.['y-coordinate'] ||
      customCoordinates?.height ||
      customCoordinates?.width
    )
  )
}

const formatCustomCoordinates = (fileObject, file) => {
  return {
    ...fileObject,
    customCoordinatesOb: {
      ['x-coordinate']: file.customCoordinates?.split(',')[0],
      ['y-coordinate']: file.customCoordinates?.split(',')[1],
      width: file.customCoordinates?.split(',')[2],
      height: file.customCoordinates?.split(',')[3],
    },
  }
}

const formatExtensions = (fileObject, extensions) => {
  const aiTagData: AutoTaggingObj[] = []
  let removeBgData: RemoveBgProps
  const extensionsData: ExtensionData = {}
  extensions.forEach((ext: Extensions) => {
    if (ext?.name === 'aws-auto-tagging' || ext?.name === 'google-auto-tagging')
      aiTagData.push(ext)

    if (ext?.name === 'remove-bg') {
      removeBgData = {
        background: ext?.options?.bg_color
          ? 'background_color'
          : ext?.options?.bg_image_url
          ? 'background_image'
          : 'no_background',
        bgColor: ext?.options?.bg_color ? ext?.options?.bg_color : '#0450d5',
        bgImageURL: ext?.options?.bg_image_url
          ? ext?.options?.bg_image_url
          : '',
        semiTransparentCheck: ext?.options?.semitransparency ?? false,
        shadowCheck: ext?.options?.add_shadow ?? false,
      }
      fileObject = { ...fileObject, removeBgData }
      extensionsData.removeBg = true
    }
    if (ext?.name === 'ai-auto-description'){
      extensionsData.autoDescription = true
    }
  })
  if (aiTagData?.length) {
    fileObject = { ...fileObject, aiTagData }
    extensionsData.aiTag = true
  }
  return { ...fileObject, extensionsData }
}

const formatCommonSettingExtensions = (fileObject, file) => {
  fileObject = { ...fileObject, extensionsData: file?.extensionsData }
  if (file?.extensionsData?.aiTag && file?.aiTagData)
    fileObject = { ...fileObject, aiTagData: file?.aiTagData }
  if (file?.extensionsData?.removeBg && file?.removeBgData)
    fileObject = {
      ...fileObject,
      removeBgData: file?.removeBgData,
    }
  return { ...fileObject }
}

const  validator = (value) => {
  if (typeof value === "string" && value.trim() === "") {
    return 'Must be non empty string with at least one non-space character.'
  }
}

export {
  metaFields,
  fileConstraints,
  hasPermission,
  addIncrementInFileNameIfRequired,
  getFolderPathForFile,
  getAllFileEntries,
  getFileFromFileEntry,
  getFileVersionFolderPath,
  getCmFieldValidatorMap,
  validateCmField,
  formatTags,
  formatCustomMetadata,
  validateCustomMetadata,
  validateCommonSettingCustomCoordinates,
  formatCustomCoordinates,
  formatExtensions,
  formatCommonSettingExtensions,
  checkCfRequired,
  validator,
}
